import { useCallback } from 'react';
import type { ContextGroup } from '@/models/template';
import type { Context } from '@/models/report';
import { useSettings } from '@/features/settings';
import { useGetWorksheetCanonicalMeasurementsQuery } from '@/state/slices/options';
import { PriorStudies } from '@/features/prior-studies';
import type { MeasurementRowModel } from '../Measurements/rowModel';
import Panel from '../shared/Panel/Panel';
import ContextSection from './Section/ContextSection';
import ContextRow from './Row/ContextRow';
import {
  imperialHeightParts,
  heightPartsToCm,
  lbsToKg,
  toImperial,
} from './utils/units';
import styles from './ContextPanel.module.css';

const FULL_ROW_THRESHOLD = 5;
/** Context classes the API sends as plain "YYYY-MM-DD" dates. */
const DATE_CONTEXT_CLASSES = new Set(['DATE_OF_BIRTH', 'STUDY_DATE']);

interface ContextPanelProps {
  contextGroups: ContextGroup[];
  context: Record<string, Context>;
  onContextChange: (contextClass: string, value: string) => void;
  /** Measurement-backed rows (HT, WT, BSA) edit through the measurement path
   *  so they save and cascade like any worksheet row. */
  rowModels?: Map<string, MeasurementRowModel>;
  /** Canonicals whose row differs from the saved report — the amber bar. */
  unsavedMeasurements?: Set<string>;
  onMeasurementChange?: (
    canonicalMeasurement: string,
    value: number | null,
    unit: string | null
  ) => void;
  onConverge?: (canonicalMeasurement: string) => void;
  disabled?: boolean;
  patientUuid?: string;
  currentStudyUuid?: string;
}

/** Empty text clears the row; anything non-numeric is ignored. */
function parseNumber(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isNaN(value) ? undefined : value;
}

export default function ContextPanel({
  contextGroups,
  context,
  onContextChange,
  rowModels,
  unsavedMeasurements,
  onMeasurementChange,
  onConverge,
  disabled = false,
  patientUuid,
  currentStudyUuid,
}: ContextPanelProps) {
  const { settings, updateSetting } = useSettings();
  const imperial = settings.unitSystem === 'IMPERIAL';
  // Only the canonical carries a formula (BSA); context labels don't.
  const { data: canonicals } = useGetWorksheetCanonicalMeasurementsQuery();

  const handleChange = useCallback(
    (contextClass: string) => (value: string) => {
      const ctx = context[contextClass];
      if (!ctx?.canonicalMeasurement) {
        onContextChange(contextClass, value);
        return;
      }
      const parsed = parseNumber(value);
      if (parsed === undefined) return;
      const metric =
        imperial && contextClass === 'WEIGHT' && parsed !== null
          ? lbsToKg(parsed)
          : parsed;
      onMeasurementChange?.(ctx.canonicalMeasurement, metric, ctx.unit ?? null);
    },
    [context, imperial, onContextChange, onMeasurementChange]
  );

  const handleHeightParts = useCallback(
    (parts: string[]) => {
      const ctx = context.HEIGHT;
      if (!ctx?.canonicalMeasurement) return;
      const cm = heightPartsToCm(parts[0], parts[1]);
      if (cm === undefined) return;
      onMeasurementChange?.(ctx.canonicalMeasurement, cm, ctx.unit ?? null);
    },
    [context, onMeasurementChange]
  );

  const priorStudiesSlot =
    patientUuid && currentStudyUuid ? (
      <PriorStudies
        patientUuid={patientUuid}
        currentStudyUuid={currentStudyUuid}
      />
    ) : undefined;

  return (
    <Panel
      title="Context"
      headerRight={priorStudiesSlot}
      collapsed={settings.collapsedContentSection}
      onCollapsedChange={(collapsed) =>
        updateSetting({ collapsedContentSection: collapsed })
      }
    >
      <div className={styles.grid}>
        {contextGroups.map((group) => {
          const isLarge = group.contextLabels.length > FULL_ROW_THRESHOLD;
          return (
            <div
              key={group.uuid}
              className={isLarge ? styles.fullRow : styles.halfRow}
            >
              <ContextSection title={group.label}>
                {group.contextLabels
                  .slice()
                  .sort((a, b) => a.index - b.index)
                  .map((cl) => {
                    const stored = context[cl.contextClass];
                    const ctx = imperial ? toImperial(stored) : stored;
                    const height =
                      imperial && cl.contextClass === 'HEIGHT'
                        ? imperialHeightParts(stored)
                        : undefined;
                    const canonical = canonicals?.find(
                      (c) => c.key === stored?.canonicalKey
                    );
                    const model = stored?.canonicalMeasurement
                      ? rowModels?.get(stored.canonicalMeasurement)
                      : undefined;
                    const calculated = model?.calculated ?? null;
                    return (
                      <ContextRow
                        key={cl.uuid}
                        label={cl.label}
                        value={ctx?.value ?? ''}
                        unit={ctx?.unit}
                        formula={canonical?.formula ?? undefined}
                        diverged={model?.divergence === 'diverged'}
                        missingInputs={model?.missingOperands}
                        unsaved={
                          !!stored?.canonicalMeasurement &&
                          !!unsavedMeasurements?.has(
                            stored.canonicalMeasurement
                          )
                        }
                        calculatedText={
                          calculated === null
                            ? null
                            : calculated.toFixed(canonical?.precision ?? 2)
                        }
                        onConverge={
                          model && onConverge && stored?.canonicalMeasurement
                            ? () => onConverge(stored.canonicalMeasurement!)
                            : undefined
                        }
                        editable={ctx?.editable ?? false}
                        onChange={handleChange(cl.contextClass)}
                        disabled={disabled}
                        isDate={DATE_CONTEXT_CLASSES.has(cl.contextClass)}
                        parts={
                          height && [
                            { value: height.feet, unit: 'ft' },
                            { value: height.inches, unit: 'in' },
                          ]
                        }
                        onPartsChange={handleHeightParts}
                      />
                    );
                  })}
              </ContextSection>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
